import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { exportNoRunDayWebIt, getNoRunDays } from "../../../services";
import { NoRunDay } from "../../../types/no-run-day.type";
import { numberWithCommas } from "../../../utils";
import { formatDateTimeBkk } from "../../../utils/date";
import { saveBlobResponse } from "../../../utils/downloadBlob";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DownloadIcon from "@mui/icons-material/Download";

// One page is plenty for a day (or the 7-day default); a longer list says so
// rather than paginating a side panel.
const NO_RUN_LIMIT = 100;
// With no day picked the list shows every report, but "every no-run day ever"
// is not a question anyone asks — the last week is. It ends YESTERDAY (Bangkok):
// today's vans may simply not have checked in yet.
const DEFAULT_RANGE_DAYS = 7;
const BKK_TZ = "Asia/Bangkok";

interface NoRunDaysSectionProps {
  selectedDate: Dayjs | null;
  selectedBranch: string;
  selectedRoute: string;
  showBranch?: boolean;
  triggerFetch?: number;
}

const rowKey = (row: NoRunDay) =>
  `${row.branch_code}|${row.route_code}|${row.stock_date}`;

/**
 * สายไม่วิ่ง: the GM uploaded the van-load but the van never checked in, so
 * there is no sales report — and no row in the table below — for accounting to
 * take the WEB_IT file from. This lists those days for the same filters, apart
 * from the report table so its totals, count and pagination stay untouched.
 */
export default function NoRunDaysSection({
  selectedDate,
  selectedBranch,
  selectedRoute,
  showBranch = false,
  triggerFetch,
}: NoRunDaysSectionProps) {
  const [rows, setRows] = useState<NoRunDay[]>([]);
  const [count, setCount] = useState<number>(0);
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  const yesterdayBkk = dayjs().tz(BKK_TZ).subtract(1, "day");
  const startDate = (
    selectedDate ?? yesterdayBkk.subtract(DEFAULT_RANGE_DAYS - 1, "day")
  ).format("YYYY-MM-DD");
  const endDate = (selectedDate ?? yesterdayBkk).format("YYYY-MM-DD");

  const fetchNoRunDays = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await getNoRunDays(
        {
          start_date: startDate,
          end_date: endDate,
          route: selectedRoute,
          branch_code: selectedBranch,
          page: 1,
          limit: NO_RUN_LIMIT,
        },
        // A side panel: if it cannot load, the report list must still read
        // clean — no toast over a page that otherwise works.
        { suppressErrorToast: true },
      );
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } catch {
      setRows([]);
      setCount(0);
    }
  }, [startDate, endDate, selectedRoute, selectedBranch]);

  useEffect(() => {
    fetchNoRunDays();
  }, [fetchNoRunDays, triggerFetch]);

  const handleDownload = async (row: NoRunDay) => {
    const key = rowKey(row);
    if (downloadingKey) return;
    setDownloadingKey(key);
    try {
      const response = await exportNoRunDayWebIt(
        row.route_code,
        row.stock_date,
        row.branch_code,
      );
      saveBlobResponse(
        response,
        `web-it-no-run-${row.route_code}-${row.stock_date}.xlsx`,
      );
    } catch {
      // Already surfaced by the axios interceptor.
    } finally {
      setDownloadingKey(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <Box
      component="section"
      aria-label="สายไม่วิ่ง"
      sx={{
        mb: 2,
        border: (theme) => `1px solid ${theme.palette.warning.main}`,
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: (theme) => theme.palette.warning.main + "1A",
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          สายไม่วิ่ง (มีของเข้าแต่ไม่มีการเช็คอิน)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {count > rows.length
            ? `แสดง ${numberWithCommas(rows.length)} จาก ${numberWithCommas(count)} รายการ`
            : `${numberWithCommas(count)} รายการ`}
          {!selectedDate &&
            ` · ${DEFAULT_RANGE_DAYS} วันล่าสุด (ถึงเมื่อวาน)`}
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ minWidth: "max-content" }}>
          <TableHead>
            <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
              <TableCell>วันที่</TableCell>
              {showBranch && <TableCell>สาขา</TableCell>}
              <TableCell>สายส่ง</TableCell>
              <TableCell>ชื่อสาย</TableCell>
              <TableCell align="right">จำนวนรายการ</TableCell>
              <TableCell align="right">ยอดรับ</TableCell>
              <TableCell align="right">ยกมา</TableCell>
              <TableCell align="right">รวม</TableCell>
              <TableCell>อัปโหลดโดย</TableCell>
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const key = rowKey(row);
              return (
                <TableRow key={key} sx={{ "& td": { whiteSpace: "nowrap" } }}>
                  {/* A bare calendar day: format as-is, no time zone. */}
                  <TableCell>
                    {dayjs(row.stock_date).format("DD/MM/YYYY")}
                  </TableCell>
                  {showBranch && <TableCell>{row.branch_code}</TableCell>}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {row.route_code}
                      <Chip
                        label="ไม่วิ่ง"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{row.route_name ?? "-"}</TableCell>
                  <TableCell align="right">
                    {numberWithCommas(row.product_count)}
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(row.withdraw_qty)}
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(row.carryover_qty)}
                  </TableCell>
                  <TableCell align="right">
                    {numberWithCommas(row.total_qty)}
                  </TableCell>
                  <TableCell>
                    {row.uploaded_by_name ?? "-"}
                    {row.uploaded_at && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        sx={{ ml: 0.5 }}
                      >
                        ({formatDateTimeBkk(row.uploaded_at)})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="ดาวน์โหลด web-it (สายไม่วิ่ง)">
                      <span>
                        <IconButton
                          size="small"
                          color="success"
                          aria-label={`ดาวน์โหลด web-it ${row.route_code} ${row.stock_date}`}
                          disabled={downloadingKey !== null}
                          onClick={() => handleDownload(row)}
                        >
                          {downloadingKey === key ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
